import type { CreateTeamPayload, UpdateTeamPayload } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"
import { useDevAuth as useAuth } from "@/hooks/use-dev-auth"

import { createTeam, destroyTeam, updateTeam } from "./api"

export function useCreateTeamMutation() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateTeamPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createTeam(context, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] })
    },
  })
}

export function useUpdateTeamMutation() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateTeamPayload }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateTeam(context, id, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] })
    },
  })
}

export function useDestroyTeamMutation() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken()
      return destroyTeam({ token: token ?? undefined }, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] })
    },
  })
}
